/**
 * Blood Test modal — custom horizontal scrollbar (Figma 17232:1614).
 *
 * A track+thumb rendered BELOW the marker table (not the table's native bar). The
 * thumb width/position reflect the table's horizontal scroll, and the thumb can be
 * dragged to scroll. Hidden when the table doesn't overflow (desktop).
 *
 * Contract, inside a .blood-test-modal:
 *   [data-bt-modal-scroll]            the scrolling table (.blood-test-modal__list)
 *   [data-bt-modal-scrollbar]         the track
 *   [data-bt-modal-scrollbar-thumb]   the thumb
 */

const MIN_THUMB = 32;

function initOne(modal) {
  const list = modal.querySelector('[data-bt-modal-scroll]');
  const bar = modal.querySelector('[data-bt-modal-scrollbar]');
  const thumb = modal.querySelector('[data-bt-modal-scrollbar-thumb]');
  if (!list || !bar || !thumb) return null;

  const update = () => {
    const { scrollWidth, clientWidth, scrollLeft } = list;
    const overflow = scrollWidth > clientWidth + 1;
    bar.style.display = overflow ? '' : 'none';
    if (!overflow) return;
    const trackW = bar.clientWidth;
    const thumbW = Math.max(MIN_THUMB, (trackW * clientWidth) / scrollWidth);
    const maxLeft = trackW - thumbW;
    const left = maxLeft * (scrollLeft / (scrollWidth - clientWidth));
    thumb.style.width = `${thumbW}px`;
    thumb.style.transform = `translateX(${left}px)`;
  };

  // ResizeObserver also fires when the <dialog> opens (list goes 0 → real width).
  const ro = new ResizeObserver(update);
  ro.observe(list);
  list.addEventListener('scroll', update, { passive: true });

  let startX = 0;
  let startScroll = 0;
  let dragging = false;

  const onMove = (e) => {
    if (!dragging) return;
    const trackW = bar.clientWidth;
    const thumbW = thumb.clientWidth;
    const denom = trackW - thumbW;
    if (denom <= 0) return;
    const ratio = (list.scrollWidth - list.clientWidth) / denom;
    list.scrollLeft = startScroll + (e.clientX - startX) * ratio;
  };
  const onUp = (e) => {
    dragging = false;
    try { thumb.releasePointerCapture(e.pointerId); } catch (_) { /* noop */ }
  };
  const onDown = (e) => {
    dragging = true;
    startX = e.clientX;
    startScroll = list.scrollLeft;
    thumb.setPointerCapture(e.pointerId);
    e.preventDefault();
  };

  thumb.addEventListener('pointerdown', onDown);
  thumb.addEventListener('pointermove', onMove);
  thumb.addEventListener('pointerup', onUp);
  thumb.addEventListener('pointercancel', onUp);

  update();

  return {
    destroy() {
      ro.disconnect();
      list.removeEventListener('scroll', update);
      thumb.removeEventListener('pointerdown', onDown);
      thumb.removeEventListener('pointermove', onMove);
      thumb.removeEventListener('pointerup', onUp);
      thumb.removeEventListener('pointercancel', onUp);
    },
  };
}

export default function initBloodTestModalScrollbar(scope) {
  const root = scope && scope.querySelectorAll ? scope : document;
  const instances = [...root.querySelectorAll('.blood-test-modal [data-bt-modal-scrollbar]')]
    .map((b) => initOne(b.closest('.blood-test-modal')))
    .filter(Boolean);

  return {
    destroy() {
      instances.forEach((i) => i.destroy());
    },
  };
}
